import {
  OUTLOOK_APPEARANCE_PROPERTY,
  formatAppearanceLocation,
  formatAppearanceSubject,
  mergeAppearanceHtml,
} from "./outlookAppearanceFormat.js";

function officeSucceeded(result) {
  const succeeded = window.Office?.AsyncResultStatus?.Succeeded;
  return result?.status === succeeded || result?.status === "succeeded";
}

function officeCall(invoker, errorMessage) {
  return new Promise((resolve, reject) => {
    invoker((result) => {
      if (officeSucceeded(result)) {
        resolve(result.value);
      } else {
        reject(new Error(result?.error?.message || errorMessage));
      }
    });
  });
}

function readProperty(property, coercionType) {
  if (!property) return Promise.resolve(null);
  if (typeof property.getAsync !== "function") return Promise.resolve(property);

  return officeCall(
    (callback) =>
      coercionType
        ? property.getAsync(coercionType, callback)
        : property.getAsync(callback),
    "Outlook could not read the appointment."
  );
}

function setProperty(property, value, options) {
  if (!property || typeof property.setAsync !== "function") {
    return Promise.reject(new Error("Open the appointment in edit mode before saving calendar changes."));
  }

  return officeCall(
    (callback) =>
      options
        ? property.setAsync(value, options, callback)
        : property.setAsync(value, callback),
    "Outlook could not update the appointment."
  );
}

function toDateInput(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toTimeInput(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  return `${`${value.getHours()}`.padStart(2, "0")}:${`${value.getMinutes()}`.padStart(2, "0")}`;
}

function dateTime(date, time) {
  const value = new Date(`${date}T${time || "09:00"}:00`);
  if (Number.isNaN(value.getTime())) {
    throw new Error("Enter a valid appearance date and time.");
  }
  return value;
}

function loadCustomProperties(item) {
  if (!item || typeof item.loadCustomPropertiesAsync !== "function") {
    return Promise.resolve(null);
  }
  return officeCall(
    (callback) => item.loadCustomPropertiesAsync(callback),
    "Outlook could not read the appointment link."
  );
}

export function isOutlookAppointment(item, Office = window.Office) {
  if (!item) return false;
  const appointmentType = Office?.MailboxEnums?.ItemType?.Appointment;
  return item.itemType === appointmentType || Boolean(item.start && item.end && item.location);
}

export function isAppointmentCompose(item) {
  return Boolean(item?.subject && typeof item.subject.setAsync === "function");
}

export async function loadOutlookAppointment(item) {
  if (!item) return null;

  const [subject, location, start, end, customProperties] = await Promise.all([
    readProperty(item.subject),
    readProperty(item.location),
    readProperty(item.start),
    readProperty(item.end),
    loadCustomProperties(item),
  ]);

  let storedAppearance = null;
  const rawAppearance = customProperties?.get?.(OUTLOOK_APPEARANCE_PROPERTY);
  if (rawAppearance) {
    try {
      storedAppearance = JSON.parse(rawAppearance);
    } catch {
      storedAppearance = null;
    }
  }

  return {
    subject: `${subject || ""}`,
    location: `${location?.displayName || location || ""}`,
    date: toDateInput(start),
    startTime: toTimeInput(start),
    endTime: toTimeInput(end),
    storedAppearance,
  };
}

export async function saveOutlookAppointment(item, appearance, { preserveDateTime = false } = {}) {
  if (!item) {
    return { mock: true };
  }
  if (!isAppointmentCompose(item)) {
    throw new Error("Open the appointment in edit mode before saving calendar changes.");
  }

  const start = preserveDateTime ? null : dateTime(appearance.date, appearance.startTime);
  const end = preserveDateTime ? null : dateTime(appearance.date, appearance.endTime);
  if (!preserveDateTime && end <= start) throw new Error("The end time must be after the start time.");

  const Office = window.Office;
  const bodyHtml = await readProperty(item.body, Office.CoercionType.Html);
  const customProperties = await loadCustomProperties(item);

  const updates = [
    setProperty(item.subject, formatAppearanceSubject(appearance)),
    setProperty(item.location, formatAppearanceLocation(appearance)),
    setProperty(
      item.body,
      mergeAppearanceHtml(bodyHtml, appearance),
      { coercionType: Office.CoercionType.Html }
    ),
  ];
  if (!preserveDateTime) {
    updates.push(setProperty(item.start, start), setProperty(item.end, end));
  }
  await Promise.all(updates);

  if (customProperties?.set && customProperties?.saveAsync) {
    customProperties.set(OUTLOOK_APPEARANCE_PROPERTY, JSON.stringify(appearance));
    await officeCall(
      (callback) => customProperties.saveAsync(callback),
      "The calendar entry was updated, but Outlook could not save its Actionstep link."
    );
  }

  return { mock: false };
}

export async function saveOutlookAppearanceMetadata(item, appearance) {
  if (!item) {
    return { mock: true };
  }
  if (!isAppointmentCompose(item)) {
    throw new Error("Open the appointment in edit mode before saving the next court date.");
  }

  const Office = window.Office;
  const [bodyHtml, customProperties] = await Promise.all([
    readProperty(item.body, Office.CoercionType.Html),
    loadCustomProperties(item),
  ]);

  await setProperty(
    item.body,
    mergeAppearanceHtml(bodyHtml, appearance),
    { coercionType: Office.CoercionType.Html }
  );

  if (customProperties?.set && customProperties?.saveAsync) {
    customProperties.set(OUTLOOK_APPEARANCE_PROPERTY, JSON.stringify(appearance));
    await officeCall(
      (callback) => customProperties.saveAsync(callback),
      "Outlook could not save the next court date to this appointment."
    );
  }

  return { mock: false };
}

export function openNextOutlookAppointment(appearance, nextCourtDate, nextCourtTime = "09:00") {
  const start = dateTime(nextCourtDate, nextCourtTime);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const mailbox = window.Office?.context?.mailbox;
  const displayNewAppointmentForm = mailbox?.displayNewAppointmentForm;

  if (typeof displayNewAppointmentForm !== "function") {
    return { mock: true, start, end, requiresUserSave: true };
  }

  displayNewAppointmentForm.call(mailbox, {
    subject: formatAppearanceSubject(appearance),
    start,
    end,
    location: formatAppearanceLocation(appearance),
    body: mergeAppearanceHtml("", {
      ...appearance,
      date: nextCourtDate,
      startTime: nextCourtTime,
      endTime: `${`${end.getHours()}`.padStart(2, "0")}:${`${end.getMinutes()}`.padStart(2, "0")}`,
      outcome: "",
    }),
  });

  return { mock: false, start, end, requiresUserSave: true };
}




